/**
 * Source-aware navigation helpers for the standalone charger flow.
 *
 * The standalone charger routes (/c/:identifier, /station,
 * /charge/active) live OUTSIDE the dashboard shell so they render
 * full-viewport. When the user enters that flow from inside the app
 * we push a small `returnTo` state onto the location so the Back
 * button on the standalone shell can send them back to the exact
 * page they came from.
 *
 * Validation is deliberately strict: only an explicit allowlist of
 * internal paths is accepted. A crafted `state.returnTo` (or any
 * future ?returnTo query param) therefore cannot bounce the user
 * into an auth boundary, a token-gated route, an external domain
 * or a `javascript:` URL. This is the same security posture the
 * ProtectedRoute redirect target already enforces — we reuse its
 * `safeInternalRedirect` gate and then narrow further to a small
 * dashboard-only root list.
 *
 * Refresh-safe: React Router `location.state` is transient. When it
 * disappears (browser refresh, new tab, share link) the Back button
 * falls through to the auth-aware default. No arbitrary return URL
 * is ever persisted to localStorage.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { safeInternalRedirect } from '../components/auth/ProtectedRoute.jsx';

// Dashboard-only route roots the Back button is allowed to jump to.
// Deliberately excludes `/auth`, `/onboard/**`, `/vehicles/change-pin/**`,
// `/activate/**`, and the standalone charger routes themselves.
const ALLOWED_ROOTS = [
  '/charge',
  '/history',
  '/vehicles',
  '/profile',
  '/settings',
  '/support',
];

// Explicit deny-list — paths that must never be a Back target
// because they either (a) belong to the standalone charger flow we
// just left (`/c`, `/station`, `/charge/active`) or (b) are token-
// gated deep-links whose token would be missing on refresh
// (`/vehicles/change-pin/**`). This list is consulted BEFORE the
// allow-list so token-gated routes can't sneak in as sub-paths of
// otherwise-allowed roots (e.g. `/vehicles/change-pin/xyz` under
// `/vehicles`).
const STANDALONE_ROOTS = [
  '/c',
  '/station',
  '/charge/active',
  '/vehicles/change-pin',
];

/** Signed-in fallback when `returnTo` is missing/rejected. */
export const SIGNED_IN_FALLBACK = '/charge';

/**
 * Signed-out fallback — the auth entry. Signed-out users hitting a
 * cold-scanned charger and pressing Back land back at the sign-in
 * form; ProtectedRoute already knows how to bounce them here anyway.
 */
export const SIGNED_OUT_FALLBACK = '/auth';

/**
 * True when `path` is an internal app path we're willing to jump
 * back to. Two layers:
 *   1. `safeInternalRedirect` rejects absolute / protocol-relative /
 *      whitespace / `javascript:` / non-string values.
 *   2. This function then narrows further to a dashboard route root.
 */
export function isAllowedReturnPath(path) {
  const safe = safeInternalRedirect(path);
  if (!safe) return false;
  // Deny standalone-flow paths first so `/charge/active` doesn't
  // sneak through the `/charge` allow rule below.
  for (const root of STANDALONE_ROOTS) {
    if (
      safe === root ||
      safe.startsWith(`${root}/`) ||
      safe.startsWith(`${root}?`)
    ) return false;
  }
  if (safe === '/') return true;
  return ALLOWED_ROOTS.some((root) => (
    safe === root ||
    safe.startsWith(`${root}/`) ||
    safe.startsWith(`${root}?`)
  ));
}

/**
 * Build the router state object to pass when navigating from inside
 * the app to a standalone charger route.
 *
 * @example
 * navigate('/c/GCEV1234567', {
 *   state: chargerReturnState({ returnTo: '/charge', source: 'user-dashboard' }),
 * });
 */
export function chargerReturnState({ returnTo, source } = {}) {
  return {
    returnTo: isAllowedReturnPath(returnTo) ? returnTo : null,
    source: typeof source === 'string' ? source : null,
  };
}

/**
 * Pure function — resolve where the Back button should send the
 * user. Priority:
 *   1. Validated `returnTo` from `location.state`.
 *   2. Signed-in fallback (dashboard Charge page) when authenticated.
 *   3. Signed-out fallback (auth entry) otherwise.
 *
 * `document.referrer` and `window.history.length` are intentionally
 * NOT consulted — both are unreliable and can be spoofed.
 */
export function resolveChargerBack({ locationState, isAuthenticated }) {
  const requested = locationState?.returnTo;
  if (isAllowedReturnPath(requested)) return requested;
  return isAuthenticated ? SIGNED_IN_FALLBACK : SIGNED_OUT_FALLBACK;
}

/**
 * React hook — returns a stable `back()` handler for the standalone
 * charger flow.
 *
 * `isPending` is true while `AuthContext` is still booting so the
 * caller can visually disable the button and never flash the wrong
 * destination for users who show up on a cold cache.
 */
export function useChargerBack() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isBooting } = useAuth();

  const back = useCallback(() => {
    if (isBooting) return;
    const target = resolveChargerBack({
      locationState: location.state,
      isAuthenticated,
    });
    navigate(target);
  }, [location.state, isAuthenticated, isBooting, navigate]);

  return { back, isPending: isBooting };
}
